import { useEffect, useState } from "react";
import { storyblokEditable, useStoryblokApi } from "@storyblok/react";

const Footer = ({ blok }) => {
  const storyblokApi = useStoryblokApi();
  const [entries, setEntries] = useState([]);

  // All footer datasource entries fetch one time with this
  useEffect(() => {
    const fetchFooterEntries = async () => {
      try {
        const { data } = await storyblokApi.get("cdn/datasource_entries", {
          datasource: "footer", // must be "footer" and is case-sensitive
          version: "published",
          per_page: 100,
        });
        setEntries(data.datasource_entries || []);
      } catch (err) {
        console.error("Error fetching footer datasource:", err);
      }
    };

    fetchFooterEntries();
  }, [storyblokApi]);

  const normalizeTokens = (fieldValue) => {
    if (!fieldValue) return [];

    const rawArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

    return rawArray
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return item.key || item.value || item.name || "";
        }
        return "";
      })
      .filter(Boolean);
  };

  // Multi-option field from Storyblok
  const getLinksFromField = (fieldValue) => {
    const tokens = normalizeTokens(fieldValue);

    return tokens
      .map((token) => {
        return entries.find(
          (e) =>
            e.key === token || // match by key
            e.value === token || // by value URL
            e.name === token // by name label
        );
      })
      .filter(Boolean);
  };

  const socialLinks = getLinksFromField(blok.social_links);
  const topLinks = getLinksFromField(blok.top_links);
  const corporateLinks = getLinksFromField(blok.corporate_links);
  const developersLinks = getLinksFromField(blok.developers_links);
  const blogLinks = getLinksFromField(blok.blog_links);
  const legalLinks = getLinksFromField(blok.legal_links);

  // Order in multi-option: Contact Us, Support, then Careers
  const contactTop = topLinks[0];
  const supportTop = topLinks[1];
  const careersTop = topLinks[2];

  // Ketch cookie settings open after the first banner is closed
  const openCookieSettings = () => {
    if (typeof window !== "undefined" && typeof window.ketch === "function") {
      window.ketch("showExperience");
    } else {
      console.warn("Ketch is not available on window yet.");
    }
  };

  // Cookie Settings button in the Legal column last item
  const cookieSettingsItem = {
    id: "cookie-settings",
    name: "Cookie Settings",
    value: "__action__",
  };
  const legalLinksWithCookie = [...legalLinks, cookieSettingsItem];

  const renderColumn = (title, items) => (
    <div>
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      <ul className="space-y-1 text-sm">
        {items.map((item) => {
          const isCookieSettings = item.id === "cookie-settings";

          return (
            <li key={item.id}>
              {isCookieSettings ? (
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="bg-transparent p-0 text-left hover:underline"
                >
                  {item.name}
                </button>
              ) : (
                <a
                  href={item.value}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline"
                >
                  {item.name}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  // Use uploaded Storyblok assets for the social icons
  const renderSocialIcon = (entry) => {
    const label = (entry.name || "").toLowerCase();

    let iconAsset = null;

    if (label.includes("facebook")) {
      iconAsset = blok.facebook_icon;
    } else if (label === "x" || label.includes("twitter")) {
      iconAsset = blok.x_icon;
    } else if (label.includes("linkedin")) {
      iconAsset = blok.linkedin_icon;
    } else if (label.includes("youtube")) {
      iconAsset = blok.youtube_icon;
    } else if (label.includes("instagram")) {
      iconAsset = blok.instagram_icon;
    }

    if (iconAsset && iconAsset.filename) {
      return (
        <img
          src={iconAsset.filename}
          alt={entry.name}
          className="h-6 w-6 md:h-7 md:w-7 object-contain"
        />
      );
    }

    // Fallback label text if icon isn't added
    return <span className="text-xs">{entry.name}</span>;
  };

  return (
    <footer
      {...storyblokEditable(blok)}
      className="w-full border-t border-gray-200 mt-16"
    >
      <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col gap-6">
        {/* Top row: social icons + Contact Us, Support, and Careers */}
        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
          {/* Column 1: social icons */}
          <div className="flex items-center gap-3 md:gap-4">
            {socialLinks.map((item) => (
              <a
                key={item.id}
                href={item.value}
                target="_blank"
                rel="noreferrer"
                aria-label={item.name}
                className="flex items-center justify-center transition-transform hover:scale-110 hover:opacity-80"
              >
                {renderSocialIcon(item)}
              </a>
            ))}
          </div>

          {/* Column 2: spacer */}
          <div className="hidden md:block" />

          {/* Columns 3 and 4 */}
          <div className="md:col-span-2 flex justify-between text-xs md:text-sm font-semibold uppercase tracking-[0.16em]">
            {contactTop && (
              <a
                href={contactTop.value}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {contactTop.name}
              </a>
            )}
            {supportTop && (
              <a
                href={supportTop.value}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {supportTop.name}
              </a>
            )}
            {careersTop && (
              <a
                href={careersTop.value}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {careersTop.name}
              </a>
            )}
          </div>
        </div>

        {/* Divider */}
        <hr className="border-gray-200" />

        {/* Middle row columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {renderColumn("Corporate", corporateLinks)}
          {renderColumn("Developers", developersLinks)}
          {renderColumn("Blog", blogLinks)}
          {renderColumn("Legal", legalLinksWithCookie)}
        </div>

        {/* Copyright */}
        <div className="mt-4 text-base text-black">
          © 2025 BlackBerry Limited. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;